import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import Intercom, {
  boot as sdkBoot,
  shutdown as sdkShutdown,
  update as sdkUpdate,
  show as sdkShow,
  hide as sdkHide,
} from "@intercom/messenger-js-sdk";

interface IntercomContextValue {
  boot: (userData?: IntercomBootData) => void;
  shutdown: () => void;
  update: (userData?: Partial<IntercomBootData>) => void;
  show: () => void;
  hide: () => void;
}

interface IntercomBootData {
  app_id?: string;
  api_base?: string;
  intercom_user_jwt?: string;
  email?: string;
  user_id?: string;
  name?: string;
  [key: string]: any;
}

interface IntercomProviderProps {
  appId: string;
  children: React.ReactNode;
  autoBootProps?: {
    verticalPadding?: number;
    horizontalPadding?: number;
    alignment?: string;
    hideDefaultLauncher?: boolean;
  };
}

const IntercomContext = createContext<IntercomContextValue | null>(null);

export const IntercomProvider: React.FC<IntercomProviderProps> = ({
  appId,
  children,
  autoBootProps = {},
}) => {
  const isInitialized = useRef(false);

  // Initialize Intercom on mount
  useEffect(() => {
    if (!isInitialized.current && appId) {
      try {
        // Initialize the SDK with app_id and config
        Intercom({
          app_id: appId,
          // Convert autoBootProps to snake_case as expected by SDK
          vertical_padding: autoBootProps.verticalPadding,
          horizontal_padding: autoBootProps.horizontalPadding,
          alignment: autoBootProps.alignment,
          hide_default_launcher: autoBootProps.hideDefaultLauncher,
        });

        isInitialized.current = true;
      } catch (error) {
        console.error("Failed to initialize Intercom:", error);
      }
    }
  }, [appId, autoBootProps]);

  // Wrapper methods that match the react-use-intercom API
  const boot = useCallback(
    (userData?: IntercomBootData) => {
      if (!userData) {
        // Boot anonymous user
        sdkBoot({
          app_id: appId,
        });
      } else {
        // Boot with user data
        sdkBoot({
          app_id: appId,
          ...userData,
        });
      }
    },
    [appId]
  );

  const shutdown = useCallback(() => {
    sdkShutdown();
  }, []);

  const update = useCallback((userData?: Partial<IntercomBootData>) => {
    if (userData) {
      sdkUpdate(userData);
    }
  }, []);

  const show = useCallback(() => {
    sdkShow();
  }, []);

  const hide = useCallback(() => {
    sdkHide();
  }, []);

  const value: IntercomContextValue = {
    boot,
    shutdown,
    update,
    show,
    hide,
  };

  return (
    <IntercomContext.Provider value={value}>
      {children}
    </IntercomContext.Provider>
  );
};

// Custom hook to use Intercom - matches the react-use-intercom API
export const useIntercom = (): IntercomContextValue => {
  const context = useContext(IntercomContext);

  if (!context) {
    // Return no-op functions if context is not available (not wrapped in provider)
    console.warn("useIntercom must be used within IntercomProvider");
    return {
      boot: () => {},
      shutdown: () => {},
      update: () => {},
      show: () => {},
      hide: () => {},
    };
  }

  return context;
};

// Export IntercomProps interface for backward compatibility
export interface IntercomProps extends IntercomBootData {}
